import { useEffect, useRef, useState, memo } from "react";
import { Spinner } from "@/components/ui/spinner";
import {
  CircleCheck,
  MinusIcon,
  XIcon,
} from "lucide-react";
import { format, parse } from "date-fns";
import { useAuth } from "@clerk/react";
import { Input } from "@/components/ui/input";
import { CopyButton } from "@/components/ui/copy-button";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useCreatorsStore } from "@/stores";
import { cn } from "@/lib/utils";
import { DynamicFieldColumnCellProps } from "../types";

const DATE_FMT = "yyyy-MM-dd";

function parseDateValue(value: string): Date | undefined {
  if (!value) return undefined;
  const parsed = parse(value, DATE_FMT, new Date());
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

function DynamicFieldColumnCell({
  creator,
  field,
}: DynamicFieldColumnCellProps) {
  const { getToken } = useAuth();
  const updateCreatorField = useCreatorsStore((s) => s.updateCreatorField);

  const fieldEntry = creator.fields?.find((f) => f.name === field.name);
  const fieldValue = fieldEntry?.value || "";
  const fieldAccepted = fieldEntry?.accepted;

  const [localValue, setLocalValue] = useState(fieldValue);
  const [isSaving, setIsSaving] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const initialValueRef = useRef(fieldValue);

  useEffect(() => {
    if (!isSaving) {
      setLocalValue(fieldValue);
      initialValueRef.current = fieldValue;
    }
  }, [fieldValue, isSaving]);

  const commit = async (
    nextValue: string,
    nextAccepted: boolean | null | undefined = undefined
  ) => {
    if (
      nextValue === initialValueRef.current &&
      nextAccepted === fieldAccepted
    ) {
      return;
    }
    setIsSaving(true);
    const token = await getToken();
    if (!token) {
      setIsSaving(false);
      return;
    }
    const ok = await updateCreatorField(
      creator._id,
      field.name,
      nextValue,
      nextAccepted,
      token
    );
    if (ok) {
      initialValueRef.current = nextValue;
    } else {
      setLocalValue(initialValueRef.current);
    }
    setIsSaving(false);
  };

  if (field.type === "string" || field.type === "number") {
    return (
      <div className="flex items-center justify-between group">
        <Input
          className="w-full rounded-r-none shadow-none border-r-0 placeholder:italic placeholder:text-xs border-transparent bg-transparent group-hover:bg-background group-hover:border-border focus-visible:bg-background focus-visible:border-primary pr-0 group-hover:shadow-xs focus-visible:shadow-xs"
          value={localValue}
          type={field.type === "number" ? "number" : "text"}
          inputMode={field.type === "number" ? "numeric" : undefined}
          placeholder="missing"
          disabled={isSaving}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={() => commit(localValue, fieldAccepted)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.blur();
            } else if (e.key === "Escape") {
              setLocalValue(initialValueRef.current);
              e.currentTarget.blur();
            }
          }}
        />
        {isSaving ? (
          <div className="size-9 flex items-center justify-center">
            <Spinner className="size-3.5 text-muted-foreground" />
          </div>
        ) : (
          <CopyButton
            buttonText=""
            tooltipText={`Copy ${field.name}`}
            textToCopy={localValue}
            variant="outline"
            className="opacity-0 group-hover:opacity-100 size-9 rounded-l-none border-l-0 border-border group-hover:shadow-xs"
            tooltipDelay={750}
          />
        )}
      </div>
    );
  }

  if (field.type === "date") {
    const dateValue = parseDateValue(localValue);
    return (
      <div className="flex items-center justify-between group">
        <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              disabled={isSaving}
              className={cn(
                "w-full justify-start font-normal h-9 px-3 rounded-r-none border border-r-0 border-transparent group-hover:bg-background group-hover:border-border group-hover:shadow-xs text-sm data-[state=open]:border-border data-[state=open]:border-r data-[state=open]:shadow-xs",
                !dateValue && "text-muted-foreground italic text-xs"
              )}
            >
              {dateValue ? format(dateValue, "PPP") : "missing"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 text-sm bg-background overflow-hidden" align="start">
            <Calendar
              captionLayout="dropdown"
              className="border-b"
              mode="single"
              selected={dateValue}
              onSelect={(d) => {
                setDatePickerOpen(false);
                if (!d) {
                  if (localValue !== "") {
                    setLocalValue("");
                    commit("", fieldAccepted);
                  }
                  return;
                }
                const formatted = format(d, DATE_FMT);
                setLocalValue(formatted);
                commit(formatted, fieldAccepted);
              }}
              autoFocus
            />
            {dateValue && (
              <div className="p-2 flex justify-end">
                <Button
                  variant="destructive-ghost"
                  size="sm"
                  className="h-7"
                  onClick={() => {
                    setDatePickerOpen(false);
                    setLocalValue("");
                    commit("", fieldAccepted);
                  }}
                >
                  Clear
                </Button>
              </div>
            )}
          </PopoverContent>
        </Popover>
        {isSaving ? (
          <div className="size-9 flex items-center justify-center">
            <Spinner className="size-3.5 text-muted-foreground" />
          </div>
        ) : (
          <CopyButton
            buttonText=""
            tooltipText={`Copy ${field.name}`}
            textToCopy={localValue}
            variant="outline"
            className="opacity-0 group-hover:opacity-100 size-9 rounded-l-none border-l-0 border-border group-hover:shadow-xs"
            tooltipDelay={750}
          />
        )}
      </div>
    );
  }

  if (field.type === "boolean") {
    const isAccepted = fieldAccepted === true && fieldValue !== "";
    const isRejected = fieldAccepted === false && fieldValue !== "";

    const currentKey: "accepted" | "rejected" | "missing" = isAccepted
      ? "accepted"
      : isRejected
        ? "rejected"
        : "missing";

    const handleChange = (next: string) => {
      if (next === currentKey) return;
      if (next === "accepted") {
        const preserved = fieldValue || "yes";
        setLocalValue(preserved);
        commit(preserved, true);
      } else if (next === "rejected") {
        const preserved = fieldValue || "no";
        setLocalValue(preserved);
        commit(preserved, false);
      } else {
        setLocalValue("");
        commit("", null);
      }
    };

    const triggerIcon = isSaving ? (
      <Spinner className="size-3.5 text-muted-foreground" />
    ) : isAccepted ? (
      <CircleCheck className="size-3.5 text-success" />
    ) : isRejected ? (
      <XIcon className="size-3.5 text-destructive" />
    ) : (
      <MinusIcon className="size-3.5 text-muted-foreground" />
    );

    const triggerLabel = isAccepted
      ? "Accepted"
      : isRejected
        ? "Rejected"
        : "missing";

    const trigger = (
      <Select
        value={currentKey}
        onValueChange={handleChange}
        disabled={isSaving}
      >
        <SelectTrigger
          size="sm"
          className={cn(
            "w-full h-9 shadow-none border border-transparent bg-transparent text-sm transition-none hover:bg-background hover:border-border hover:shadow-xs focus-visible:bg-background focus-visible:border-primary focus-visible:shadow-xs",
            "group-hover/bool:bg-background group-hover/bool:border-border group-hover/bool:shadow-xs",
            "data-[state=open]:bg-background data-[state=open]:border-border data-[state=open]:shadow-xs",
            "[&>svg:last-child]:opacity-0 group-hover/bool:[&>svg:last-child]:opacity-50 data-[state=open]:[&>svg:last-child]:opacity-50 [&>svg:last-child]:transition-opacity",
            !isAccepted && !isRejected && "text-muted-foreground italic text-xs"
          )}
        >
          <span className="flex items-center gap-2">
            {triggerIcon}
            {triggerLabel}
          </span>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="accepted">
            <CircleCheck className="size-3.5 text-success" />
            Accepted
          </SelectItem>
          <SelectItem value="rejected">
            <XIcon className="size-3.5 text-destructive" />
            Rejected
          </SelectItem>
          <SelectItem value="missing">
            <MinusIcon className="size-3.5 text-muted-foreground" />
            Missing: <span className="text-xs text-muted-foreground">Unset all values</span>
          </SelectItem>
        </SelectContent>
      </Select>
    );

    return (
      <div className="group/bool flex items-center">
        {fieldValue ? (
          <Tooltip delayDuration={300}>
            <TooltipTrigger asChild>
              <div className="w-full">{trigger}</div>
            </TooltipTrigger>
            <TooltipContent>
              <p>{fieldValue}</p>
            </TooltipContent>
          </Tooltip>
        ) : (
          trigger
        )}
      </div>
    );
  }

  return null;
}

export default memo(DynamicFieldColumnCell);
